if (!customElements.get('td-bundle-product-switcher')) {
  const SELECTORS = {
    link: '[data-td-bundle-sibling-link]',
    bundleSection: '[data-td-bundle-section]',
    linkedSection: '[data-td-bundle-linked-section][data-section-id]',
    stickyBundleSiblingSelect: '[data-sticky-atc-bundle-sibling-select]',
  };

  const DATA_KEYS = {
    sectionId: 'tdSectionId',
    currentProductHandle: 'tdCurrentProductHandle',
    productUrl: 'tdProductUrl',
    productHandle: 'tdProductHandle',
  };

  const prefetchCache = new Map();

  class TdBundleProductSwitcher extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset[DATA_KEYS.sectionId];
      this.currentHandle = this.dataset[DATA_KEYS.currentProductHandle];
      this.saveData = window.navigator?.connection?.saveData;
      this.sectionWrapper = document.querySelector(
        `${SELECTORS.bundleSection}[data-section-id="${this.sectionId}"]`
      );
      this.links = [];

      this.handleClick = this.handleClick.bind(this);
      this.handleHover = this.handleHover.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.unlockSectionAfterTransition = this.unlockSectionAfterTransition.bind(this);
      this.unlockTimer = null;
    }

    connectedCallback() {
      this.sectionWrapper = document.querySelector(
        `${SELECTORS.bundleSection}[data-section-id="${this.sectionId}"]`
      );
      this.currentHandle = this.dataset[DATA_KEYS.currentProductHandle];
      this.links = Array.from(this.querySelectorAll(SELECTORS.link));

      this.links.forEach((link) => {
        link.addEventListener('click', this.handleClick);
        link.addEventListener('mouseenter', this.handleHover);
        link.addEventListener('focus', this.handleFocus);
      });

      window.addEventListener('popstate', this.handlePopState);
    }

    disconnectedCallback() {
      this.links.forEach((link) => {
        link.removeEventListener('click', this.handleClick);
        link.removeEventListener('mouseenter', this.handleHover);
        link.removeEventListener('focus', this.handleFocus);
      });

      window.removeEventListener('popstate', this.handlePopState);
    }

    handleClick(event) {
      const link = event.currentTarget;
      const targetHandle = link.dataset[DATA_KEYS.productHandle];

      if (!targetHandle || targetHandle === this.currentHandle) {
        // Let the browser follow the current-product link normally so any
        // query-string or stateful URL changes on the anchor still work.
        return;
      }

      event.preventDefault();
      this.switchToProductUrl(link.dataset[DATA_KEYS.productUrl], targetHandle);
    }

    handleHover(event) {
      this.prefetchProduct(event.currentTarget);
    }

    handleFocus(event) {
      this.prefetchProduct(event.currentTarget);
    }

    handlePopState() {
      this.switchToProductUrl(window.location.href, '', { updateHistory: false, allowReload: true });
    }

    buildRequestUrl(productUrl) {
      return this.buildRequestUrlWithIds(productUrl, this.getLinkedSectionIds());
    }

    buildRequestUrlWithIds(productUrl, sectionIds) {
      const requestUrl = new URL(productUrl, window.location.origin);
      requestUrl.searchParams.set('sections', sectionIds.join(','));
      return requestUrl.toString();
    }

    getLinkedSectionElements() {
      return Array.from(document.querySelectorAll(SELECTORS.linkedSection));
    }

    getLinkedSectionIds() {
      return this.getLinkedSectionElements()
        .map((section) => section.dataset.sectionId)
        .filter(Boolean);
    }

    prefetchProduct(link) {
      if (this.saveData) return;

      const productHandle = link.dataset[DATA_KEYS.productHandle];
      const productUrl = link.dataset[DATA_KEYS.productUrl];
      const linkedSectionIds = this.getLinkedSectionIds();
      const cacheKey = `${productHandle || productUrl}:${linkedSectionIds.join(',')}`;

      if (!productUrl || prefetchCache.has(cacheKey)) {
        return;
      }

      const request = this.fetchSections(productUrl, linkedSectionIds, `prefetch sibling bundle: ${productHandle || productUrl}`)
        .then((sectionsById) => {
          if (!Object.keys(sectionsById).length) {
            prefetchCache.delete(cacheKey);
            return {};
          }

          prefetchCache.set(cacheKey, sectionsById);
          return sectionsById;
        })
        .catch((error) => {
          prefetchCache.delete(cacheKey);
          console.error(error);
          // Empty-string sentinel indicates the prefetch failed; callers
          // already treat falsey/non-object results as a reload fallback.
          return '';
        });

      prefetchCache.set(cacheKey, request);
    }

    switchToProductUrl(productUrl, productHandle, options = {}) {
      const { updateHistory = true, allowReload = true } = options;
      const linkedSectionElements = this.getLinkedSectionElements();
      const linkedSectionIds = linkedSectionElements
        .map((section) => section.dataset.sectionId)
        .filter(Boolean);
      const cacheKey = `${productHandle || productUrl}:${linkedSectionIds.join(',')}`;

      const sectionMarkupPromise = prefetchCache.has(cacheKey)
        ? Promise.resolve(prefetchCache.get(cacheKey))
        : this.fetchSections(productUrl, linkedSectionIds, `fetch sibling bundle: ${productHandle || productUrl}`);

      sectionMarkupPromise
        .then((sectionsById) => {
          if (!sectionsById || !Object.keys(sectionsById).length || !this.sectionWrapper) {
            if (allowReload) {
              window.location.href = productUrl;
            }
            return;
          }

          const currentHeight = this.sectionWrapper.offsetHeight;

          this.sectionWrapper.style.minHeight = `${currentHeight}px`;
          this.sectionWrapper.classList.add('is-switching');

          this.preloadSectionsImages(sectionsById)
            .catch(() => Promise.resolve())
            .then(() => {
              linkedSectionElements.forEach((sectionElement) => {
                const targetSectionId = sectionElement.dataset.sectionId;
                const sectionHtml = sectionsById[targetSectionId];

                if (!sectionHtml) {
                  return;
                }

                const nextInnerHtml = this.getSectionInnerHTML(sectionHtml, targetSectionId);
                if (!nextInnerHtml) {
                  return;
                }

                // We only inject markup returned by Shopify's same-origin section
                // rendering endpoint, so this HTML is trusted server output here.
                sectionElement.innerHTML = nextInnerHtml;
                sectionElement.dispatchEvent(
                  new CustomEvent('section-content::changed', {
                    bubbles: true,
                    detail: targetSectionId,
                  })
                );
              });

              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  if (!this.sectionWrapper) return;
                  this.sectionWrapper.classList.remove('is-switching');
                  this.sectionWrapper.addEventListener('transitionend', this.unlockSectionAfterTransition, { once: true });
                  this.unlockTimer = window.setTimeout(this.unlockSectionAfterTransition, 250);
                });
              });

              if (updateHistory) {
                window.history.pushState({ url: productUrl }, '', productUrl);
              }

              this.syncStickyBundleSiblingSelect(productUrl, productHandle);
            });
        })
        .catch((error) => {
          console.error(error);
          this.unlockSectionAfterTransition();
          if (allowReload) {
            window.location.href = productUrl;
          }
        });
    }

    unlockSectionAfterTransition() {
      if (this.unlockTimer) {
        window.clearTimeout(this.unlockTimer);
        this.unlockTimer = null;
      }
      if (!this.sectionWrapper) return;
      this.sectionWrapper.style.minHeight = '';
      this.sectionWrapper.classList.remove('is-switching');
    }

    syncStickyBundleSiblingSelect(productUrl, productHandle) {
      const stickySelect = document.querySelector(SELECTORS.stickyBundleSiblingSelect);

      if (!stickySelect) {
        return;
      }

      const matchingOption = Array.from(stickySelect.options || []).find((option) => {
        if (productHandle && option.dataset.stickyAtcBundleProductHandle === productHandle) {
          return true;
        }

        return option.value === productUrl;
      });

      if (!matchingOption) {
        return;
      }

      stickySelect.value = matchingOption.value;
      Array.from(stickySelect.options || []).forEach((option) => {
        option.selected = option === matchingOption;
      });
    }

    fetchSections(productUrl, linkedSectionIds, contextLabel) {
      return fetch(this.buildRequestUrlWithIds(productUrl, linkedSectionIds))
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to ${contextLabel}`);
          }

          return response.json();
        })
        .then((sections) => sections || {})
        .catch((error) => {
          console.warn(error);
          return this.fetchSectionsIndividually(productUrl, linkedSectionIds);
        });
    }

    fetchSectionsIndividually(productUrl, linkedSectionIds) {
      const sectionRequests = linkedSectionIds.map((sectionId) => {
        const requestUrl = new URL(productUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', sectionId);

        return fetch(requestUrl.toString())
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch section ${sectionId}`);
            }

            return response.text();
          })
          .then((sectionHtml) => [sectionId, sectionHtml])
          .catch((error) => {
            console.warn(error);
            return [sectionId, ''];
          });
      });

      return Promise.all(sectionRequests).then((entries) => {
        return entries.reduce((sectionsById, [sectionId, sectionHtml]) => {
          if (sectionHtml) {
            sectionsById[sectionId] = sectionHtml;
          }

          return sectionsById;
        }, {});
      });
    }

    getSectionInnerHTML(html, sectionId) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = parsedDocument.querySelector(
        `${SELECTORS.linkedSection}[data-section-id="${sectionId}"]`
      );
      return nextSection ? nextSection.innerHTML : html;
    }

    preloadSectionsImages(sectionsById) {
      const preloadPromises = Object.values(sectionsById).map((sectionHtml) => this.preloadSectionImages(sectionHtml));
      return Promise.all(preloadPromises);
    }

    preloadSectionImages(html) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const images = Array.from(parsedDocument.images || []).filter((image) => image.currentSrc || image.src);

      if (!images.length) {
        return Promise.resolve();
      }

      return Promise.all(
        images.map((image) => {
          const source = image.currentSrc || image.src;

          if (!source) {
            return Promise.resolve();
          }

          return new Promise((resolve) => {
            const preloadImage = new Image();
            preloadImage.onload = resolve;
            preloadImage.onerror = resolve;
            preloadImage.src = source;
          });
        })
      );
    }
  }

  customElements.define('td-bundle-product-switcher', TdBundleProductSwitcher);
}
